import { jsx, jsxs } from "react/jsx-runtime";
import { useState, useContext, useEffect } from "react";
import styled, { css } from "styled-components";
import { useHover } from "@react-aria/interactions";
import { useFocusRing } from "@react-aria/focus";
import { useLayer } from "react-laag";
import { IconCheck, IconArrowRight } from "@bynder/icons";
import { textStyles } from "./textStyles.js";
import { token } from "./token.js";
import { Checkbox } from "./Checkbox.js";
import { useId } from "./useId.js";
import { useKeyDown } from "./useKeyDown.js";
import { Switch } from "./Switch.js";
import { Container } from "./Container.js";
import { useDropdownContext } from "./context3.js";
import { ItemContext } from "./itemContext.js";
import { fixLayerProps } from "./fixLayerProps.js";
function Item({
  children,
  isDisabled,
  icon,
  checkedVariant = "checkmark",
  isChecked = false,
  badge,
  onClick: onClickProp,
  submenu,
  role: roleProp,
  ...props
}) {
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const [isSubmenuOpenWithKeyboard, setIsSubmenuOpenWithKeyboard] = useState(false);
  const id = useId("dropdown-item");
  const labelId = useId("dropdown-item-label");
  const submenuId = useId("dropdown-submenu");
  const hasSubmenu = Boolean(submenu);
  const {
    dispatch,
    ...dropdown
  } = useDropdownContext();
  const isHighlighted = dropdown.highlightedItemId === id;
  const parentId = useContext(ItemContext);
  const {
    isFocusVisible,
    focusProps
  } = useFocusRing();
  const {
    hoverProps
  } = useHover({
    onHoverStart: () => {
      if (hasSubmenu) {
        setIsSubmenuOpen(true);
      }
    },
    onHoverEnd: () => {
      if (hasSubmenu) {
        setIsSubmenuOpen(false);
      }
    }
  });
  const shouldDisplaySubmenu = isSubmenuOpen && hasSubmenu;
  const {
    triggerProps,
    layerProps,
    renderLayer
  } = useLayer({
    isOpen: shouldDisplaySubmenu,
    auto: true,
    placement: "right-start",
    possiblePlacements: ["right-end", "right-start", "left-start", "left-end"],
    triggerOffset: -4,
    onParentClose: () => {
    }
  });
  fixLayerProps(layerProps);
  useEffect(() => {
    dispatch({
      type: "add-item",
      payload: {
        id,
        parentId,
        isDisabled,
        isSelected: checkedVariant === "checkmark" ? isChecked : void 0
      }
    });
  });
  useEffect(() => {
    if (isSubmenuOpenWithKeyboard) {
      dispatch({
        type: "highlight-submenu"
      });
    }
  }, [dispatch, isSubmenuOpenWithKeyboard]);
  useEffect(() => {
    var _a;
    if (dropdown.hasSearch || dropdown.isKeepingFocusOnTrigger) {
      return;
    }
    if (isHighlighted) {
      (_a = document.querySelector("#" + id)) == null ? void 0 : _a.focus();
    }
  }, [dropdown.hasSearch, dropdown.isKeepingFocusOnTrigger, id, isHighlighted]);
  useKeyDown("ArrowLeft", (event) => {
    if (hasSubmenu && isSubmenuOpenWithKeyboard) {
      dispatch({
        type: "highlight-item",
        payload: id
      });
      setIsSubmenuOpen(false);
      setIsSubmenuOpenWithKeyboard(false);
      event.preventDefault();
    }
  });
  useKeyDown("ArrowRight", (event) => {
    if (isHighlighted && hasSubmenu) {
      setIsSubmenuOpen(true);
      setIsSubmenuOpenWithKeyboard(true);
      event.preventDefault();
    }
  });
  const className = getClassName(checkedVariant);
  const role = getRole(roleProp, checkedVariant, isChecked);
  const ariaChecked = role === "menuitemcheckbox" || role === "menuitemradio" || role === "option" ? isChecked : void 0;
  const submenuTriggerProps = hasSubmenu ? {
    "aria-haspopup": true,
    "aria-expanded": isSubmenuOpen,
    "aria-controls": submenuId
  } : {};
  function onClick(event) {
    onClickProp == null ? void 0 : onClickProp(event);
    if (hasSubmenu) {
      return;
    }
    if (role === "menuitemcheckbox") {
      dispatch({
        type: "keep-highlight"
      });
      return;
    }
    dropdown.close();
  }
  function onKeyDown(event) {
    if (hasSubmenu) {
      if (event.key === " " || event.key === "Enter") {
        onClickProp == null ? void 0 : onClickProp(event);
        setIsSubmenuOpen((prev) => {
          const newState = !prev;
          setIsSubmenuOpenWithKeyboard(newState);
          return newState;
        });
        event.preventDefault();
      }
    }
  }
  return /* @__PURE__ */ jsx(ItemContext.Provider, {
    value: id,
    children: /* @__PURE__ */ jsxs(ItemWrapper, {
      ...hoverProps,
      ...triggerProps,
      onClick: (e) => e.stopPropagation(),
      children: [/* @__PURE__ */ jsxs(ItemStyled, {
        disabled: isDisabled,
        "aria-disabled": isDisabled,
        $isChecked: isChecked,
        "aria-checked": ariaChecked,
        "aria-selected": dropdown.hasSearch || dropdown.isKeepingFocusOnTrigger ? isHighlighted : void 0,
        role,
        onClick,
        tabIndex: -1,
        id,
        onKeyDown,
        $isHighlighted: isHighlighted || isFocusVisible,
        $isSubmenuOpen: isSubmenuOpen,
        className,
        ...submenuTriggerProps,
        ...focusProps,
        ...props,
        onMouseEnter: () => hasSubmenu && setIsSubmenuOpen(true),
        children: [checkedVariant === "checkbox" && /* @__PURE__ */ jsx(CheckboxContainer, {
          onClick: (e) => e.stopPropagation(),
          children: /* @__PURE__ */ jsx(Checkbox, {
            isChecked,
            isDisabled,
            "aria-labelledby": labelId,
            onChange: onClick
          })
        }), icon && /* @__PURE__ */ jsx(Icon, {
          children: icon
        }), /* @__PURE__ */ jsx(Label, {
          id: labelId,
          children
        }), checkedVariant === "checkmark" && isChecked && /* @__PURE__ */ jsx(RightContainer, {
          children: /* @__PURE__ */ jsx(IconCheck, {})
        }), badge && /* @__PURE__ */ jsx(RightContainer, {
          children: badge
        }), checkedVariant === "switch" && /* @__PURE__ */ jsx(RightContainer, {
          onClick: (e) => e.stopPropagation(),
          children: /* @__PURE__ */ jsx(Switch, {
            isChecked,
            isDisabled,
            "aria-labelledby": labelId,
            onChange: onClick
          })
        }), hasSubmenu && /* @__PURE__ */ jsx(SubmenuIconContainer, {
          $isActive: isSubmenuOpen || isFocusVisible || isHighlighted,
          children: /* @__PURE__ */ jsx(IconArrowRight, {})
        })]
      }), shouldDisplaySubmenu && renderLayer(/* @__PURE__ */ jsx(SubMenuContainer, {
        "data-testid": "ds-dropdown-submenu",
        id: submenuId,
        ...layerProps,
        onMouseLeave: () => setIsSubmenuOpen(false),
        children: submenu
      }))]
    })
  });
}
function getRole(roleProp, checkedVariant, isChecked) {
  if (roleProp) {
    return roleProp;
  }
  if (checkedVariant === "checkbox" || checkedVariant === "switch") {
    return "menuitemcheckbox";
  }
  if (checkedVariant === "checkmark" && isChecked) {
    return "menuitemradio";
  }
  return "menuitem";
}
function getClassName(checkedVariant) {
  if (checkedVariant === "checkbox") {
    return Checkbox.pointerEventsClassname;
  }
  if (checkedVariant === "switch") {
    return Switch.pointerEventsClassname;
  }
  return "";
}
const ItemWrapper = styled.div.withConfig({
  displayName: "ItemWrapper",
  componentId: "sc-1ihd0z9-0"
})(["position:relative;"]);
const ItemStyled = styled.button.withConfig({
  displayName: "ItemStyled",
  componentId: "sc-1ihd0z9-1"
})(["", " display:flex;align-items:center;box-sizing:border-box;width:100%;height:36px;margin:0;padding:0 ", ";border:none;background:none;color:", ";outline:none;font-family:", ";cursor:pointer;", " ", " &:hover{background:", ";color:", ";}&:active{background:", ";color:", ";}&:disabled{background:none;color:", ";cursor:default;}"], textStyles.uiTextS, token.spacing5, (props) => props.$isChecked ? token.colorText : token.colorTextMuted, token.brandFontStackBase, (props) => props.$isSubmenuOpen && css(["background:", ";color:", ";"], token.gray25, token.gray800), (props) => props.$isHighlighted && css(["background:", ";color:", ";"], token.gray50, token.gray800), token.gray25, token.colorText, token.gray50, token.gray800, token.colorTextDisabled);
const CheckboxContainer = styled.span.withConfig({
  displayName: "CheckboxContainer",
  componentId: "sc-1ihd0z9-2"
})(["display:flex;flex:none;justify-content:center;align-items:center;width:24px;height:24px;margin-right:", ";"], token.spacing3);
const Icon = styled.span.withConfig({
  displayName: "Icon",
  componentId: "sc-1ihd0z9-3"
})(["display:flex;flex:none;justify-content:center;align-items:center;width:24px;height:24px;margin-right:", ";"], token.spacing3);
const Label = styled.span.withConfig({
  displayName: "Label",
  componentId: "sc-1ihd0z9-4"
})(["flex:1;overflow:hidden;min-width:0;text-align:left;text-overflow:ellipsis;white-space:nowrap;"]);
const RightContainer = styled.span.withConfig({
  displayName: "RightContainer",
  componentId: "sc-1ihd0z9-5"
})(["flex:none;margin:0 ", ";&:last-child{margin-right:calc(", " * -1);}"], token.spacing3, token.spacing2);
const SubmenuIconContainer = styled.span.withConfig({
  displayName: "SubmenuIconContainer",
  componentId: "sc-1ihd0z9-6"
})(["flex:none;margin-right:calc(", " * -1);margin-left:", ";color:", ";", ":hover,", ":active{color:", ";}"], token.spacing3, token.spacing3, (props) => props.$isActive ? token.gray400 : token.gray200, ItemStyled, ItemStyled, token.gray400);
const SubMenuContainer = styled(Container).withConfig({
  displayName: "SubMenuContainer",
  componentId: "sc-1ihd0z9-7"
})(["margin-top:calc(", " * -1);"], token.spacing3);
export {
  Item
};
//# sourceMappingURL=Item4.js.map
